/**
 * HearingApprovalEntry — the client's card for a hearing approval (docs/HEARING_OUTCOMES.md; Matt,
 * 2026-10-23: "it demarks an important moment in the project's life... some kind of animated
 * fireworks or something on the card are in order. Something tasteful and on-brand but exciting").
 *
 * Posted by the Permitting Coordinator's hearing card (api/_hearings.js), never authored here. Shows
 * the board, the date, the vote, the conditions, and what happens next (api/_hearingWords.js, the same
 * words the client email uses). The appeal status updates in place when the period ends.
 *
 * The fireworks: a few slow bursts in the brand's own colors, the first time this card is the one in
 * view on this device, then still. Replayable from a quiet link. None for reduced motion.
 */
import { useEffect, useRef, useState } from 'react'
import { J, fmtJourneyDate } from '../journeyUI.jsx'
import { nextSteps, voteLine } from '../../../../api/_hearingWords.js'

const COLORS = ['#f0b27a', '#e2b866', '#d98f5c', '#9fc2b4', '#c9a27a']

function useFireworks(canvasRef, run) {
  useEffect(() => {
    if (!run) return
    const cv = canvasRef.current
    if (!cv) return
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const ctx = cv.getContext('2d')
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const W = cv.clientWidth, H = cv.clientHeight
    cv.width = W * dpr; cv.height = H * dpr; ctx.scale(dpr, dpr)
    const parts = []
    // Five bursts, staggered, above and around the word; each a ring of fine sparks with a trail.
    const bursts = [
      { t: 150, x: 0.30, y: 0.30 }, { t: 700, x: 0.72, y: 0.22 }, { t: 1250, x: 0.50, y: 0.16 },
      { t: 1850, x: 0.18, y: 0.18 }, { t: 2350, x: 0.84, y: 0.32 },
    ]
    let fired = 0, raf = 0
    const t0 = performance.now()
    function burst(b) {
      const n = 34, color = COLORS[Math.floor(Math.random() * COLORS.length)], color2 = COLORS[Math.floor(Math.random() * COLORS.length)]
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + Math.random() * 0.15
        const sp = 2.6 + Math.random() * 1.6
        parts.push({ x: b.x * W, y: b.y * H, px: b.x * W, py: b.y * H, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, decay: 0.011 + Math.random() * 0.006, c: Math.random() < 0.75 ? color : color2, w: 1.1 + Math.random() * 0.6 })
      }
    }
    function frame(now) {
      const el = now - t0
      while (fired < bursts.length && el >= bursts[fired].t) burst(bursts[fired++])
      ctx.clearRect(0, 0, W, H)
      ctx.lineCap = 'round'
      for (const p of parts) {
        if (p.life <= 0) continue
        p.px = p.x; p.py = p.y
        p.vx *= 0.965; p.vy = p.vy * 0.965 + 0.045
        p.x += p.vx; p.y += p.vy; p.life -= p.decay
        const a = Math.max(0, p.life)
        // a streak from where it was, so each spark reads as light rather than confetti
        ctx.globalAlpha = a
        ctx.strokeStyle = p.c; ctx.lineWidth = p.w
        ctx.beginPath(); ctx.moveTo(p.px - p.vx * 2.5, p.py - p.vy * 2.5); ctx.lineTo(p.x, p.y); ctx.stroke()
        if (a > 0.6) { ctx.fillStyle = '#fff'; ctx.globalAlpha = (a - 0.6) * 1.5; ctx.beginPath(); ctx.arc(p.x, p.y, p.w * 0.9, 0, Math.PI * 2); ctx.fill() }
      }
      ctx.globalAlpha = 1
      if (fired < bursts.length || parts.some(p => p.life > 0)) raf = requestAnimationFrame(frame)
      else ctx.clearRect(0, 0, W, H)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [run])   // eslint-disable-line react-hooks/exhaustive-deps
}

export default function HearingApprovalEntry({ entry, isActive }) {
  const d = entry.data || {}
  const canvasRef = useRef(null)
  const [play, setPlay] = useState(0)
  const seenKey = 'hearing-cheer:' + entry.id
  useEffect(() => {
    if (!isActive) return
    let seen = false
    try { seen = !!localStorage.getItem(seenKey) } catch { /* private mode: play it */ }
    if (seen) return
    try { localStorage.setItem(seenKey, '1') } catch { /* ignore */ }
    setPlay(p => p + 1)
  }, [isActive])   // eslint-disable-line react-hooks/exhaustive-deps
  useFireworks(canvasRef, play)

  const conditions = Array.isArray(d.conditions) ? d.conditions.filter(Boolean) : []
  const vote = voteLine(d.vote)
  const steps = nextSteps({ outcome: d.outcome, appealEnds: d.appealEndsOn, storyPoles: d.storyPoles, conditions, appealStatus: d.appealStatus })
  const final = d.appealStatus === 'none'

  return (
    <div style={{ position: 'relative' }}>
      <canvas ref={canvasRef} aria-hidden="true" style={{ position: 'absolute', left: 0, top: -30, width: '100%', height: 300, pointerEvents: 'none', zIndex: 1 }} />
      <div style={{ position: 'relative', textAlign: 'center', paddingTop: 10 }}>
        <div style={{ ...J.eyebrow, marginBottom: 18 }}>{d.bodyName || 'Design Review'}</div>
        <div style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300, fontSize: 'clamp(30px, 4.6vw, 44px)', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#07272D', lineHeight: 1.2 }}>
          Approved
        </div>
        <div style={{ ...J.mono, marginTop: 14 }}>
          {fmtJourneyDate(d.hearingDate)}{vote ? ' · ' + vote : ''}
        </div>
        {final && <div style={{ ...J.mono, marginTop: 6, color: '#2f7d62' }}>Final · no appeals</div>}
        <div style={{ width: 36, height: 1, background: '#f0b27a', margin: '26px auto 26px' }} />
      </div>

      <div style={{ ...J.body, maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
        The {d.bodyName || 'board'} approved your project. Design review is one of the most demanding steps on the way to a building permit, and you are through it. Congratulations.
      </div>

      {conditions.length > 0 && (
        <div style={{ maxWidth: 520, margin: '28px auto 0' }}>
          <div style={{ ...J.eyebrow, marginBottom: 10 }}>{conditions.length === 1 ? 'Condition of approval' : 'Conditions of approval'}</div>
          {conditions.map((c, i) => (
            <div key={i} style={{ ...J.body, fontSize: 14, padding: '8px 2px', borderBottom: '0.5px solid rgba(7,39,45,0.12)', display: 'flex', gap: 10 }}>
              <span style={{ ...J.mono, width: 22, flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</span><span>{c}</span>
            </div>
          ))}
        </div>
      )}

      {steps.length > 0 && (
        <div style={{ maxWidth: 520, margin: '28px auto 0' }}>
          <div style={{ ...J.eyebrow, marginBottom: 10 }}>What happens next</div>
          {steps.map((s, i) => (
            <div key={i} style={{ ...J.body, fontSize: 14, lineHeight: 1.6, padding: '6px 0 6px 16px', position: 'relative' }}>
              <span style={{ position: 'absolute', left: 0, top: 15, width: 5, height: 5, borderRadius: '50%', background: '#f0b27a' }} />{s}
            </div>
          ))}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 26 }}>
        <button type="button" onClick={() => setPlay(p => p + 1)} style={{ ...J.textLink, background: 'none', border: 'none', borderBottom: '0.5px solid rgba(7,39,45,0.3)', cursor: 'pointer', padding: '2px 0' }}>
          Celebrate again
        </button>
      </div>
    </div>
  )
}
